/**
 * The standing rule, and the short list it does not cover.
 *
 * **CIDER warns; it does not refuse.** A soft check ends in a sentence that names what is wrong,
 * a **Proceed anyway** button, and the name of whoever pressed it with a one-line reason —
 * audited, and on the record afterward: _"Proceeded by Dan Kowalski · 'Vendor's truck is
 * here'"_. The festival is run by people standing in a field at 4 PM on a Saturday, and a
 * system that will not let them past a rule it got slightly wrong is a system they work around
 * on paper.
 *
 * The exceptions are here, and there are five of them. Each is a refusal because a **law or a
 * policy** says so, not because CIDER thinks it knows better; each names that law, so the list
 * cannot quietly grow. Anything that refuses and is not on this list is a defect
 * (`tools/standing-rule.test.ts` holds the whole product to it).
 *
 * A hard stop still says who to call. A door slammed with no name behind it is the same dead
 * end as a warning nobody can pass.
 */

export interface HardStop {
  /** The id the code raises it by. */
  id: string;
  /** The check, in the words a person would use. */
  what: string;
  /** The law or the policy that makes this a refusal and not a warning. */
  law: string;
  /** What the person reads on the screen. */
  sentence: string;
  /** Who can actually settle it — never a dead end. */
  whoToCall: string;
}

export const HARD_STOPS: readonly HardStop[] = [
  {
    id: 'safe_environment',
    what: 'A safe-environment requirement that is switched on for this person’s scope',
    law: 'Archdiocese of Detroit safe-environment policy, as the year’s own switches set it',
    sentence:
      'The archdiocese’s safe-environment requirement is not met for this person, and it is switched on for this role. This one cannot be passed.',
    whoToCall: 'the safe-environment coordinator',
  },
  {
    id: 'pub_under_21',
    what: 'Somebody under 21 on a Pub shift',
    law: 'Michigan liquor law · MCL 436.1701',
    sentence:
      'Nobody under 21 works the Pub. Put them on another area — the Bakery and the Games tents both take the hours.',
    whoToCall: 'the volunteer chairs',
  },
  {
    id: 'raffle_online',
    what: 'A raffle ticket sold online',
    law: 'Michigan charitable gaming license · the license is for tickets sold in person',
    sentence:
      'Raffle tickets are sold in person only — the festival’s license does not cover an online sale. The page can say where and when to buy one.',
    whoToCall: 'the finance director',
  },
  {
    id: 'two_names_on_a_count',
    what: 'Sealing a count with fewer than two names, or with one person twice',
    law: 'The festival’s own money rule (ADR 0004) — the counter never seals their own count',
    sentence:
      'A count needs two names before it can be sealed, and the person who counted is not one of them.',
    whoToCall: 'a director who did not count',
  },
  {
    id: 'second_director_on_a_check',
    what: 'A check over the year’s tier with one director’s signature',
    law: 'The festival’s own money rule (ADR 0004) — two directors sign, always',
    sentence: 'A check over the year’s amount needs a second director. This one has one signature.',
    whoToCall: 'another director',
  },
];

export const HARD_STOP_IDS: readonly string[] = HARD_STOPS.map((h) => h.id);

/** Is this refusal one of the five? Everything else warns and can be passed with a name. */
export function isHardStop(id: string): boolean {
  return HARD_STOP_IDS.includes(id);
}

export function hardStop(id: string): HardStop | null {
  return HARD_STOPS.find((h) => h.id === id) ?? null;
}

/** What somebody did when they passed a warning — kept on the record, not only in the audit. */
export interface Proceeded {
  /** The person, by name. Never an id on a screen. */
  byName: string;
  /** One line, in their own words. A blank one is not a reason and the check refuses it. */
  reason: string;
  at: Date;
}

/** `Proceeded by Dan Kowalski · "Vendor's truck is here"` — the line a record carries. */
export function proceedLine(p: Proceeded): string {
  return `Proceeded by ${p.byName} · “${p.reason.trim()}”`;
}

/**
 * What a soft check answers with: the sentence, and that it can be passed. A caller that hands
 * back `canProceed: false` has to name the hard stop, or this is a defect.
 */
export interface Warning {
  id: string;
  says: string;
  canProceed: boolean;
  /** Set where `canProceed` is false — the law, and who to call. */
  because: HardStop | null;
}

export function warn(id: string, says: string): Warning {
  const stop = hardStop(id);
  return stop
    ? { id, says: stop.sentence, canProceed: false, because: stop }
    : { id, says, canProceed: true, because: null };
}
